import type { TFunction } from 'i18next'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ErroApi } from '../api/client'
import { painel as api, type CanaisDoPainel, type CanalDoPainel, type SituacaoDoCanal } from '../api/painel'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import { formatar } from '../i18n'

/**
 * A situação de cada canal (ficha 311).
 *
 * Existe por causa de 05/10/2026: um número de WhatsApp parou de receber às
 * 13h33 e só foi notado 26 horas depois, por um cliente. O "sinal de vida" de
 * cima dizia que o atendimento estava de pé — e estava, nos outros canais. O
 * que faltava era a linha **por canal**.
 *
 * ⭐ **Carrega sozinho, separado do resto do painel.** Se esta leitura falhar, o
 * sinal de vida e as métricas continuam na tela: um bloco que não abre não pode
 * levar o painel inteiro junto.
 *
 * 🔴 **"Não conferido" não é "fora do ar"** — e por isso não é vermelho. A tela
 * mostra o que o back decidiu; não reinterpreta.
 */
export function PainelCanais() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const [dados, setDados] = useState<CanaisDoPainel | null>(null)
  const [falhou, setFalhou] = useState(false)
  const [conferindo, setConferindo] = useState(false)
  const [recado, setRecado] = useState<{ tom: 'info' | 'perigo'; texto: string } | null>(null)

  const carregar = useCallback(async () => {
    setFalhou(false)
    try {
      setDados(await api.canais())
    } catch {
      setFalhou(true)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  async function conferir() {
    setRecado(null)
    setConferindo(true)

    try {
      const novo = await api.conferirCanais()
      setDados(novo)
      setFalhou(false)

      // `conferiu: false` = já havia uma conferência em andamento; o que veio é o gravado.
      setRecado({
        tom: 'info',
        texto: t(novo.conferiu === false ? 'painel.canais.jaConferindo' : 'painel.canais.conferido'),
      })
    } catch (erro) {
      setRecado({
        tom: 'perigo',
        texto: t(
          erro instanceof ErroApi && erro.status === 403
            ? 'painel.canais.semPermissaoParaConferir'
            : 'painel.canais.conferirFalhou',
        ),
      })
    } finally {
      setConferindo(false)
    }
  }

  const grupos = dados ? porMarca(dados.canais) : []
  const fora = dados?.canais.filter((c) => c.situacao === 'ForaDoAr') ?? []

  return (
    <Cartao
      titulo={t('painel.canais.titulo')}
      acoes={
        pode('ParametroGerenciar') && (
          <Botao variante="discreto" onClick={conferir} carregando={conferindo}>
            {t('painel.canais.conferirAgora')}
          </Botao>
        )
      }
    >
      {recado && <Aviso tom={recado.tom}>{recado.texto}</Aviso>}

      {falhou && !dados ? (
        <Estado tipo="erro" acao={<Botao onClick={carregar}>{t('comum.tentarDeNovo')}</Botao>} />
      ) : !dados ? (
        <Estado tipo="carregando" />
      ) : dados.canais.length === 0 ? (
        <Estado tipo="vazio" descricao={t('painel.canais.nenhum')} />
      ) : (
        <>
          {fora.length > 0 && (
            <Aviso tom="perigo" titulo={t('painel.canais.foraDoArTitulo', { count: fora.length })}>
              {fora.map((c) => c.nome).join(' · ')}
            </Aviso>
          )}

          {!dados.alertaLigado && <Aviso tom="alerta">{t('painel.canais.alertaDesligado')}</Aviso>}

          {grupos.map(([marca, canais]) => (
            <section key={marca} className="painel__marca" aria-label={marca}>
              <h3 className="painel__marca-nome">{marca}</h3>

              <ul className="painel__indicadores">
                {canais.map((c) => (
                  <Canal key={c.id} canal={c} agora={dados.em} t={t} />
                ))}
              </ul>
            </section>
          ))}
        </>
      )}
    </Cartao>
  )
}

function Canal({ canal: c, agora, t }: { canal: CanalDoPainel; agora: string; t: TFunction }) {
  const detalhes = [c.tipo, c.telefone, c.assistente ?? t('painel.canais.semAssistente')].filter(Boolean)

  return (
    <li
      className={`painel__indicador painel__indicador--${classe(c.situacao)} ${
        c.ativa ? '' : 'painel__canal--desligado'
      }`}
      data-situacao={c.situacao}
    >
      <p className="painel__indicador-nome">
        {/* UI-09: símbolo e rótulo em texto vêm do back junto com a situação —
            a cor da borda nunca carrega a informação sozinha. */}
        <span aria-hidden="true">{c.simbolo}</span> {c.nome}
        <span className="painel__indicador-rotulo">{c.rotulo}</span>
      </p>

      <p className="painel__indicador-detalhe">{detalhes.join(' · ')}</p>

      <p className="painel__indicador-texto">{c.motivo}</p>

      {c.oQueFazer && (
        <p className="painel__indicador-detalhe">
          <strong>{t('painel.canais.oQueFazer')}</strong> {c.oQueFazer}
        </p>
      )}

      {c.alerta?.naoEnviado && (
        <p className="painel__canal-aviso">
          <span aria-hidden="true">!</span> {t('painel.canais.alertaNaoEnviado', { motivo: c.alerta.naoEnviado })}
        </p>
      )}

      {c.alerta && c.alerta.tipo !== 'Nenhum' && c.alerta.ultimoAvisoEm && (
        <p className="painel__indicador-detalhe">
          {t('painel.canais.avisoEnviado', { quando: formatar.dataHora(c.alerta.ultimoAvisoEm) })}
        </p>
      )}

      <p className="painel__indicador-detalhe">
        {[
          c.ultimaRecebidaEm
            ? t('painel.canais.ultimaRecebida', { ha: haQuanto(c.ultimaRecebidaEm, agora, t) })
            : t('painel.canais.nuncaRecebeu'),
          c.ultimaEnviadaEm
            ? t('painel.canais.ultimaEnviada', { ha: haQuanto(c.ultimaEnviadaEm, agora, t) })
            : t('painel.canais.nuncaEnviou'),
          c.conferidoEm
            ? t('painel.canais.conferidoEm', { quando: formatar.dataHora(c.conferidoEm) })
            : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
    </li>
  )
}

/** Agrupa por marca, na ordem em que o back mandou. */
function porMarca(canais: CanalDoPainel[]): [string, CanalDoPainel[]][] {
  const grupos = new Map<string, CanalDoPainel[]>()

  for (const c of canais) grupos.set(c.marca, [...(grupos.get(c.marca) ?? []), c])

  return [...grupos.entries()]
}

/**
 * As quatro cores que o painel já usa. "Não conferido" e "desligada" são
 * neutros de propósito: nenhum dos dois é defeito conhecido.
 */
function classe(situacao: SituacaoDoCanal) {
  return (
    {
      EmOrdem: 'verde',
      Atencao: 'atencao',
      ForaDoAr: 'vermelho',
      NaoConferido: 'desconhecida',
      Desligado: 'desconhecida',
    } as const
  )[situacao]
}

/**
 * "há 26 h", "há 3 dias". O "agora" é o do SERVIDOR, que veio na resposta: o
 * relógio do computador de quem olha pode estar errado, e num painel de
 * plantão "há 5 min" errado é pior que nenhum número.
 */
function haQuanto(iso: string, agora: string, t: TFunction) {
  const minutos = Math.max(0, Math.floor((new Date(agora).getTime() - new Date(iso).getTime()) / 60_000))

  if (minutos < 1) return t('painel.canais.haInstantes')
  if (minutos < 60) return t('painel.canais.haMinutos', { total: minutos })

  const horas = Math.floor(minutos / 60)
  if (horas < 48) return t('painel.canais.haHoras', { total: horas })

  return t('painel.canais.haDias', { total: Math.floor(horas / 24) })
}
